// frontend/components/providers/ClientLayoutWrapper.tsx

"use client";

import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { ThemeProvider } from "next-themes";

// Filter out the React 19 next-themes script tag hydration warning in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  const orig = console.error;
  console.error = (...args: any[]) => {
    if (typeof args[0] === 'string' && args[0].includes('Encountered a script tag')) {
      return;
    }
    orig.apply(console, args);
  };
}

import Sidebar from "../Sidebar";
import AuthProvider from "./AuthProvider";
import SideViewPanel from "../sidebar/SideViewPanel";
import AdminNotificationBell from "../notifications/AdminNotificationBell";
import LegacyReminderMigration from "./LegacyReminderMigration";

/** 인증된 사용자에게 SSE 스트림을 연결하고 sidebar-refresh 이벤트를 window로 relay합니다. */
function SseConnector() {
  const { data: session } = useSession();
  const token = (session?.user as any)?.accessToken as string | undefined;

  useEffect(() => {
    if (!token) return;
    // 운영 HTTPS는 Caddy, 로컬 HTTP는 Next rewrite가 각각 같은 오리진으로 전달한다.
    const ssePath = window.location.protocol === "https:"
      ? "/api/system/events"
      : "/xapi/system/events";
    const sseUrl = `${window.location.origin}${ssePath}`;
    const es = new EventSource(`${sseUrl}?token=${encodeURIComponent(token)}`);
    es.addEventListener("sidebar-refresh", () => {
      window.dispatchEvent(new Event("force-sidebar-refresh"));
    });
    es.addEventListener("comment-update", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        window.dispatchEvent(new CustomEvent("comment-updated", { detail: { docId: data.docId } }));
      } catch {}
    });
    // 잠금 상태 변경 중계.
    // 같은 문서를 열어 둔 다른 탭이 동결하거나 A4 편집을 시작하면 새로고침 없이 반영됨.
    es.addEventListener("lock-change", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        window.dispatchEvent(new CustomEvent("doc-lock-changed", { detail: { docId: data.docId } }));
      } catch {}
    });
    es.addEventListener("notification", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        window.dispatchEvent(new CustomEvent("show-toast", {
          detail: {
            message: data.message,
            action: data.action ? {
              label: data.action.label,
              onClick: () => {
                if (data.action.url) {
                  window.location.href = data.action.url;
                }
              }
            } : undefined
          }
        }));
      } catch {}
    });
    // 서버가 알림을 적재하면 알림함에 갱신 신호만 보낸다.
    // 내용은 실어 보내지 않는다 — 알림함이 열려 있을 때만 목록을 다시 조회하면 되고,
    // 닫혀 있으면 배지 숫자만 갱신하면 되기 때문. (폴링 없이 이 신호로만 움직인다)
    es.addEventListener("notification-inbox", () => {
      window.dispatchEvent(new Event("notification-inbox"));
    });
    return () => es.close();
  }, [token]);

  return null;
}

// 상태 관리 및 라우팅 감지가 필요한 클라이언트 전용 래퍼 컴포넌트
export default function ClientLayoutWrapper({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  const [sideDocId, setSideDocId] = useState<string | null>(null);
  const [isEmbed, setIsEmbed] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarCollapsedRef = useRef(false);

  useLayoutEffect(() => {
    setIsEmbed(document.documentElement.classList.contains("embed-mode"));
    // useSearchParams()는 App Router에서 Suspense 경계를 요구하는데, 이 컴포넌트는
    // 앱 전체를 감싸는 루트라 그럴 수 없음 — 대신 window.location에서 직접 읽는다.
    const stored = localStorage.getItem("sidebar-collapsed") === "true";
    sidebarCollapsedRef.current = stored;
    setSidebarCollapsed(stored);
  }, []);

  useEffect(() => {
    const handleOpenSideView = (e: Event) => {
      const docId = (e as CustomEvent).detail?.docId ?? null;
      setSideDocId(docId);
    };
    window.addEventListener("open-side-view", handleOpenSideView);
    return () => window.removeEventListener("open-side-view", handleOpenSideView);
  }, []);

  // 사이드바 토글 이벤트 수신 (Sidebar의 << 버튼, DocTopBar의 ≡ 버튼)
  useEffect(() => {
    const handleToggle = () => {
      const next = !sidebarCollapsedRef.current;
      sidebarCollapsedRef.current = next;
      localStorage.setItem("sidebar-collapsed", String(next));
      setSidebarCollapsed(next);
      window.dispatchEvent(new CustomEvent("sidebar-state-changed", { detail: { collapsed: next } }));
    };
    window.addEventListener("sidebar-toggle", handleToggle);
    return () => window.removeEventListener("sidebar-toggle", handleToggle);
  }, []);

  const isAuthPage = ["/login", "/register", "/change-password", "/request-password-reset"].includes(pathname || "");
  const showSidebar = !isAuthPage && !isEmbed;

  return (
    <AuthProvider>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem themes={["light", "dark", "abyte"]}>
        <SseConnector />
        <LegacyReminderMigration />
        <div className="flex h-screen w-screen overflow-hidden bg-white dark:bg-[#191919] text-gray-950 dark:text-gray-100 transition-colors duration-200 print:h-auto print:w-auto print:overflow-visible print:block">

          {/* 사이드바: collapsed 시 너비 0으로 숨김. 인쇄 시에는 항상 숨김 */}
          <div
            data-sidebar
            className="shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out print:hidden"
            style={{ width: showSidebar ? (sidebarCollapsed ? 0 : "18.125rem") : 0 }}
          >
            {showSidebar && <Sidebar />}
          </div>

          {/* 메인 콘텐츠 + 사이드 패널 */}
          <div className="flex-1 flex overflow-hidden min-w-0 print:block print:overflow-visible">
            {/* 메인 콘텐츠 */}
            <div className="flex-1 overflow-y-auto min-w-0 print:overflow-visible print:h-auto">
              {children}
            </div>

            {/* 사이드 보기 패널 (우측 슬라이드). 인쇄 시에는 항상 숨김 */}
            <div
              className="overflow-hidden border-l border-gray-200 dark:border-zinc-800 transition-[width] duration-300 ease-in-out shrink-0 print:hidden"
              style={{ width: sideDocId ? "50%" : 0 }}
            >
              {sideDocId && (
                <SideViewPanel
                  docId={sideDocId}
                  onClose={() => setSideDocId(null)}
                />
              )}
            </div>
          </div>

        </div>

        {/* 관리자 알림 종 — 인증 페이지·임베드 모드·PDF 렌더 페이지에선 숨김 */}
        {!isAuthPage && !isEmbed && <AdminNotificationBell />}

      </ThemeProvider>
    </AuthProvider>
  );
}
